import { useRef } from "react";
import {
  Navigate, useLocation, useNavigate, useSearchParams,
} from "react-router";
import { toast } from "sonner";
import { USER_PROFILE_PATH, USER_PROFILE_PROFIL_TAB } from "@/features/userProfile/routes";
import {
  getPendingFormRedirect,
  isMandatoryGuardExemptedPath,
  showMandatoryCustomFormToast,
} from "@/router/mandatoryGuard";
import { MandatoryFieldsToast } from "@/shared/components/MandatoryFieldsToast";
import { useUserMandatoryFieldsCheck } from "@/shared/hooks/useUserMandatoryFieldsCheck";

export type MandatoryFieldsGuardProps = React.PropsWithChildren;

const MandatoryFieldsGuard = ({ children }: MandatoryFieldsGuardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    hasMandatoryMissingFields, mandatoryMissingFields, pendingMandatoryCustomForm, isLoading,
  } = useUserMandatoryFieldsCheck();
  const hasShownToastRef = useRef(false);
  const hasShownFormToastRef = useRef(false);

  const navigateToProfile = () => {
    navigate(USER_PROFILE_PROFIL_TAB);
  };

  if (isLoading) {
    return null;
  }

  const isExemptedPath = isMandatoryGuardExemptedPath(location.pathname);

  if (location.pathname === USER_PROFILE_PATH && hasMandatoryMissingFields) {
    const currentTab = searchParams.get("tab") || "profil";

    if (currentTab !== "profil") {
      if (!hasShownToastRef.current) {
        toast.custom(
          (t) => (
            <MandatoryFieldsToast
              toastId={t}
              missingFields={mandatoryMissingFields}
              onNavigateToProfile={navigateToProfile}
              onDismiss={() => {
                hasShownToastRef.current = false;
              }}
            />
          ),
          {
            id: "mandatory-fields-toast",
            duration: 8000,
          },
        );
        hasShownToastRef.current = true;
      }

      return (
        <Navigate
          to={USER_PROFILE_PROFIL_TAB}
          replace
          state={{ from: location.pathname, reason: "mandatory_fields_missing" }}
        />
      );
    }
  }

  if (hasMandatoryMissingFields && !isExemptedPath) {
    if (!hasShownToastRef.current) {
      toast.custom(
        (t) => (
          <MandatoryFieldsToast
            toastId={t}
            missingFields={mandatoryMissingFields}
            onNavigateToProfile={navigateToProfile}
            onDismiss={() => {
              hasShownToastRef.current = false;
            }}
          />
        ),
        {
          id: "mandatory-fields-toast",
          duration: 8000,
        },
      );
      hasShownToastRef.current = true;
    }

    return (
      <Navigate
        to={USER_PROFILE_PROFIL_TAB}
        replace
        state={{ from: location.pathname, reason: "mandatory_fields_missing" }}
      />
    );
  }

  if (!hasMandatoryMissingFields && hasShownToastRef.current) {
    hasShownToastRef.current = false;
  }

  // Formulaire obligatoire après connexion : ne passe qu'après le profil
  // (adresse, téléphone, attributs), qui a la priorité ci-dessus.
  // Tant que le profil est incomplet, le formulaire attend : le profil, ouvert pour
  // être complété, ne l'est pas au formulaire, et les deux se renverraient le salarié.
  const pendingFormPath = hasMandatoryMissingFields
    ? null
    : getPendingFormRedirect(location.pathname, pendingMandatoryCustomForm);

  if (pendingFormPath && pendingMandatoryCustomForm) {
    // Une seule fois par redirection (URL tapée, lien profond) ; la sidebar
    // bloque ses propres clics en amont, donc pas de doublon avec son toast.
    if (!hasShownFormToastRef.current) {
      showMandatoryCustomFormToast({
        form: pendingMandatoryCustomForm,
        onNavigateToForm: () => navigate(pendingFormPath),
      });
      hasShownFormToastRef.current = true;
    }

    return (
      <Navigate
        to={pendingFormPath}
        replace
        state={{ from: location.pathname, reason: "mandatory_custom_form_pending" }}
      />
    );
  }

  // Arrivé sur le formulaire (ou sur un chemin exempté) : la prochaine
  // redirection pourra de nouveau prévenir.
  hasShownFormToastRef.current = false;

  return <>{children}</>;
};

export default MandatoryFieldsGuard;
